import axios, { AxiosResponse, AxiosError } from 'axios';
import { ioTrack } from '@/utils/ioTrack';
import { formErrorReason } from '@/utils/formErrorReason';
import {
  FormDataInterface,
  SubmissionOptionsInterface,
  ConfirmOptionsInterface,
  HelperOptionsInterface,
  ErrorDataInterface,
  SuccessResultInterface,
  SubmissionResultType,
  AlertType,
  CounterInterface,
} from '../interfaces/MeterReadingFormInterface';

// Default options for the helper functions
const defaultOptions: Required<HelperOptionsInterface> = {
  successMessage: 'Zählerstand erfolgreich übermittelt.',
  errorMessage: 'Beim Übermitteln des Zählerstands ist ein Fehler aufgetreten.',
  retryErrorMessage: 'Beim Übermitteln des Zählerstands ist ein Fehler aufgetreten.<br>Bitte versuchen Sie es später erneut.',
};

export function destroyAllNotify(): void {
  if (window.GlobalEvents && window.GlobalEvents.destroy) {
    window.GlobalEvents.destroy('success');
    window.GlobalEvents.destroy('error');
    window.GlobalEvents.destroy('warning');
    window.GlobalEvents.destroy('info');
  }
}

/**
 * Fire alert using GlobalEvents
 * @param type - Alert type (success, error, warning, info)
 * @param message - Alert message
 */
export function fireAlert(type: AlertType, message: string): void {
  if (window.GlobalEvents && window.GlobalEvents.fire) {
    destroyAllNotify();
    window.GlobalEvents.fire('alert', [{
      type,
      message,
    }]);
  }
}

/**
 * Process form data and convert to FormData for submission
 * @param formData - The raw form data
 * @param options - Processing options
 * @returns FormData object ready for submission
 */
export function processFormData(
  formData: FormDataInterface,
  options: SubmissionOptionsInterface = {},
): FormData {
  const processedData = new FormData();

  const hasReadings = formData.readings && Object.values(formData.readings)
    .some((value) => value !== null && value !== undefined && value !== '' && Number(value) > 0);

  // Handle readings
  if (formData.readings && hasReadings) {
    Object.entries(formData.readings).forEach(([key, value]) => {
      processedData.append(`readings[${key}]`, String(value));
    });
  }

  // Handle image upload
  const hasImage = formData.image
    && (Array.isArray(formData.image) ? formData.image.length > 0 : true);

  if (hasImage) {
    // Only add image if:
    // 1. No readings provided, OR
    // 2. Readings provided AND readingsAndImage option is true
    if (!hasReadings || options.readingsAndImage) {
      if (Array.isArray(formData.image)) {
        processedData.append('image', formData.image[0]);
      } else if (formData.image) {
        processedData.append('image', formData.image);
      }
    }
  }

  // Handle reading date - convert from reading_date to readingDate
  if (formData.reading_date) {
    processedData.append('readingDate', String(formData.reading_date));
  } else if (formData.readingDate) {
    processedData.append('readingDate', String(formData.readingDate));
  }

  // Determine and add reading type
  let readingType = '';
  const imageWillBeIncluded = hasImage && (!hasReadings || options.readingsAndImage);

  if (hasReadings && imageWillBeIncluded) {
    readingType = 'both';
  } else if (hasReadings) {
    readingType = 'reading';
  } else if (imageWillBeIncluded) {
    readingType = 'image';
  }

  if (readingType) {
    processedData.append('readingType', readingType);
  }

  // Handle any other form fields
  Object.entries(formData).forEach(([key, value]) => {
    if (!['readings', 'image', 'reading_date', 'readingDate'].includes(key)) {
      if (value !== null && value !== undefined) {
        processedData.append(key, String(value));
      }
    }
  });

  return processedData;
}

/**
 * Handle successful response
 * @param response - Axios response
 * @param options - Handler options
 * @param helperOptions - Helper options for messages
 * @returns Success result
 */
export function handleResponse(
  response: AxiosResponse,
  options: SubmissionOptionsInterface = {},
  helperOptions: HelperOptionsInterface = {},
): SuccessResultInterface {
  const { data } = response;
  const mergedOptions = { ...defaultOptions, ...helperOptions };

  if (
    options.onConfirm
    && typeof options.onConfirm === 'function'
    && Object.prototype.hasOwnProperty.call(data, 'confirm')
  ) {
    options.onConfirm(data.readings);
    return { success: true, needsConfirm: true, readings: data.readings };
  }

  // Handle success
  let message = mergedOptions.successMessage;
  if (Object.prototype.hasOwnProperty.call(data, 'additional')) {
    message += ` ${data.additional}`;
  }

  fireAlert('success', message);

  if (options.onSuccess && typeof options.onSuccess === 'function') {
    options.onSuccess(response);
  }

  return { success: true, data, message };
}

/**
 * Handle error response
 * @param error - Axios error
 * @param options - Handler options
 * @param helperOptions - Helper options for messages
 * @returns Error data
 */
export function handleError(
  error: AxiosError,
  options: SubmissionOptionsInterface = {},
  helperOptions: HelperOptionsInterface = {},
): ErrorDataInterface {
  const mergedOptions = { ...defaultOptions, ...helperOptions };

  // Nur Nutzer-Fehler mit Grund; technische Fehler erfasst io.center per base_monitoring.
  const reason = formErrorReason(error, 'meter_reading');
  if (reason) {
    ioTrack('portal.energy', 'meter_reading.form_error', reason);
  }

  const errorData: ErrorDataInterface = {
    success: false,
    error,
    errors: {},
  };

  if (error.response && error.response.data) {
    // Handle validation errors
    if ((error.response.data as any).errors) {
      const processedErrors: Record<string, string> = {};
      Object.entries((error.response.data as any).errors).forEach(([key, val]) => {
        processedErrors[key] = Array.isArray(val) ? val[0] : String(val);
      });
      errorData.errors = processedErrors;
    }

    // Handle error messages
    const errorMessage = (error.response.data as any).message
      || (error.response.data as any).error;
    if (errorMessage) {
      fireAlert('error', errorMessage);
    }
  }

  // Fire generic error message
  fireAlert('error', mergedOptions.errorMessage);

  if (options.onError && typeof options.onError === 'function') {
    options.onError(errorData);
  }

  return errorData;
}

/**
 * Submit form data with readings and/or images
 * @param formData - The form data object
 * @param action - The endpoint URL
 * @param options - Additional options
 * @param helperOptions - Helper options for messages
 * @returns Promise with submission result
 */
export function submitForm(
  formData: FormDataInterface,
  action: string,
  options: SubmissionOptionsInterface = {},
  helperOptions: HelperOptionsInterface = {},
): Promise<SubmissionResultType> {
  const processedFormData = processFormData(formData, options);

  ioTrack('portal.energy', 'meter_reading.form_submit');

  return axios.post(action, processedFormData)
    .then((response: AxiosResponse) => handleResponse(response, options, helperOptions))
    .catch((error: AxiosError) => handleError(error, options, helperOptions));
}

/**
 * Submit confirmation request
 * @param actionConfirm - Confirmation endpoint
 * @param options - Options
 * @param helperOptions - Helper options for messages
 * @returns Promise with confirmation result
 */
export function submitConfirm(
  actionConfirm: string,
  options: ConfirmOptionsInterface = {},
  helperOptions: HelperOptionsInterface = {},
): Promise<{ success: boolean; error?: any }> {
  const mergedOptions = { ...defaultOptions, ...helperOptions };

  return axios.get(actionConfirm, { params: options.params })
    .then((response) => {
      const message = mergedOptions.successMessage;
      fireAlert('success', message);

      if (options.onSuccess && typeof options.onSuccess === 'function') {
        options.onSuccess(response);
      }

      return { success: true };
    })
    .catch((error: any) => {
      const errorMessage = (error.response.data?.error || error.response.data?.message) ?? mergedOptions.retryErrorMessage;
      fireAlert('error', errorMessage);

      if (options.onError && typeof options.onError === 'function') {
        options.onError(error);
      }

      return { success: false, error };
    });
}

/**
 * Validate form data
 * @param formData - Form data to validate
 * @param counters - Counter configuration (optional)
 * @returns Whether the form data is valid
 */
export function validateFormData(
  formData: FormDataInterface,
  counters: CounterInterface[] = [],
): boolean {
  const hasDate = formData.readingDate || formData.reading_date;
  const hasReadings = formData.readings && Object.keys(formData.readings).length > 0
    && Object.values(formData.readings).every((reading) => reading !== null && reading !== '' && Number(reading) !== 0);
  const hasImage = formData.image
    && (Array.isArray(formData.image) ? formData.image.length > 0 : true);

  // If counters are provided, check if all counters have readings
  if (counters.length > 0 && hasReadings) {
    const readingKeys = Object.keys(formData.readings || {});
    const counterIds = counters.map((counter) => counter.id);
    const hasAllReadings = counterIds.every((id) => readingKeys.includes(id.toString()));

    return Boolean(hasDate && ((hasAllReadings && hasReadings) || hasImage));
  }

  return Boolean(hasDate && (hasReadings || hasImage));
}
